// @ts-nocheck
import { useMemo, useState, useEffect, useRef, useCallback } from "react";
import {
  Card,
  Row,
  Col,
  Form,
  Button,
  InputGroup,
  OverlayTrigger,
  Tooltip,
  Modal,
} from "react-bootstrap";
import {
  ArrowsCollapse,
  ArrowsExpand,
  FilePdf,
  ChevronUp,
  ChevronDown,
  InfoCircleFill,
} from "react-bootstrap-icons";
import { NumericFormat } from "react-number-format";
import "./DescricaoDespesa.css";
import { FaEye } from "react-icons/fa";
import {
  downloadDespesaByIdentificador,
  downloadArquivoDespesaByArquivoId,
} from "../../../../../services/FinanceiroService";

const uuid = () =>
  ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
    (
      c ^
      (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))
    ).toString(16),
  );

const formatDateBR = (val) => {
  if (!val) return "";
  const dt = typeof val === "string" ? new Date(val) : val;
  if (Number.isNaN(dt?.getTime?.())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  const dd = pad(dt.getDate());
  const mm = pad(dt.getMonth() + 1);
  const yyyy = dt.getFullYear();
  const hh = pad(dt.getHours());
  const mi = pad(dt.getMinutes());
  return `${dd}/${mm}/${yyyy} ${hh}:${mi}`;
};

const normalizeText = (v) =>
  String(v ?? "")
    .trim()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();

const normalizeStatus = (status) => {
  if (status === "CONFORME" || status === "NAO_CONFORME") return status;

  if (typeof status === "string") {
    const s = status
      .normalize("NFD")
      .replace(/\p{Diacritic}/gu, "")
      .trim()
      .toLowerCase();

    if (["conforme", "aprovado", "ok", "aceito"].includes(s)) return "CONFORME";
    if (
      [
        "nao_conforme",
        "nao conforme",
        "não conforme",
        "reprovado",
        "nc",
      ].includes(s)
    )
      return "NAO_CONFORME";
  }
  return null;
};

const isoToInputDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const toLocalDatetimeInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  const yyyy = d.getFullYear();
  const mm = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());
  const hh = pad(d.getHours());
  const mi = pad(d.getMinutes());
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
};

const orderHistorico = (hist = []) => {
  return [...hist].sort((a, b) => {
    const ad = a?.dataSolicitacao || a?.dataCorrecao || null;
    const bd = b?.dataSolicitacao || b?.dataCorrecao || null;

    const aHas = !!ad;
    const bHas = !!bd;

    if (aHas && bHas) {
      return new Date(ad).getTime() - new Date(bd).getTime();
    }
    if (aHas && !bHas) return 1;
    if (!aHas && bHas) return -1;
    return (a?.id ?? 0) - (b?.id ?? 0);
  });
};

const extractCorrecoes = (item) => {
  const hist = Array.isArray(item?.historicoAnaliseAdequacaoDespesa)
    ? item.historicoAnaliseAdequacaoDespesa
    : [];

  const somenteComSolicitacao = orderHistorico(hist).filter((h) => {
    const ds = h?.dataSolicitacao;
    if (ds == null || String(ds).trim() === "") return false;
    const d = new Date(ds);
    return !Number.isNaN(d.getTime());
  });

  return somenteComSolicitacao.map((h) => ({
    solicitadoEmISO: h?.dataSolicitacao ?? null,
    corrigidoEmISO: h?.dataCorrecao ?? null,
    solicitadoEm: isoToInputDate(h?.dataSolicitacao ?? null),
    corrigidoEm: isoToInputDate(h?.dataCorrecao ?? null),
    obsAnalista: h?.obsAnalista ?? "",
    obsSindicato: h?.obsSindicato ?? "",
    conforme: typeof h?.conforme === "boolean" ? h.conforme : null,
    _raw: h,
  }));
};

function DescricaoDespesa({
  TiposDespesas,
  DadosDespesas,
  onChange,
  reloadKey,
  invalidObsIds,
  parcelas,
  comprovantes,
  bloquearEdicao = false,
}) {
  const [despesas, setDespesas] = useState([]);
  const initializedRef = useRef(false);
  const reloadKeyRef = useRef(reloadKey);

  const opcoes = useMemo(
    () =>
      (TiposDespesas || []).map((t) => ({
        value: t.id,
        label: t.descricao,
      })),
    [TiposDespesas],
  );

  const tipoOrderMap = useMemo(() => {
    const map = new Map();
    (opcoes || []).forEach((o, idx) => map.set(String(o.value), idx));
    return map;
  }, [opcoes]);

  const isRetornadoParaAnalista = (h) =>
    !!h?.dataSolicitacao &&
    !!h?.dataCorrecao &&
    h?.conforme === false &&
    h?.correcaoRealizada === true;

  const parcelaJaSolicitada = useCallback(
    (idTipoParcela) => {
      const isJaSolicitadoParcela = (p) =>
        normalizeText(p?.situacao) === "ja solicitado";

      return (parcelas || []).some(
        (p) =>
          Number(p?.idTipoParcela) === Number(idTipoParcela) &&
          isJaSolicitadoParcela(p),
      );
    },
    [parcelas],
  );

  useEffect(() => {

    const reloadKeyChanged = reloadKey !== reloadKeyRef.current;
    reloadKeyRef.current = reloadKey;

    if (initializedRef.current && !reloadKeyChanged) return;

    const origemLocal = Array.isArray(DadosDespesas)
      ? DadosDespesas
      : DadosDespesas?.comprovantesDespesas || [];

    const comprovantesFiltrados = Array.isArray(comprovantes)
      ? comprovantes.filter((comp) => {
          const primeiraSolicitada =
            !!comp?.primeiraParcela && parcelaJaSolicitada(1);
          const segundaSolicitada =
            !!comp?.segundaParcela && parcelaJaSolicitada(2);
          return !primeiraSolicitada && !segundaSolicitada;
        })
      : [];

    const listaBase =
      comprovantesFiltrados.length > 0 ? comprovantesFiltrados : origemLocal;

    const normalizar = (item, originalIndex) => {
      const idComprovanteDespesa =
        Number(item?.id ?? item?.idComprovanteDespesa) || 0;

      const historico = Array.isArray(item.historicoAnaliseAdequacaoDespesa)
        ? item.historicoAnaliseAdequacaoDespesa
        : [];

      const lastAnalise = historico.reduce(
        (acc, h) => (Number(h?.id) > Number(acc?.id || 0) ? h : acc),
        null,
      );

      const idTipo =
        item.idTipoDespesa ?? item.tipoDespesaId ?? item.idTipo ?? null;

      const labelTipo =
        item.descricao ||
        opcoes.find((o) => String(o.value) === String(idTipo))?.label ||
        "";

      const fileObj = item.file
        ? item.file
        : item.nomeArquivo
          ? { name: item.nomeArquivo, base64: null }
          : item.nomeComprovante
            ? { name: item.nomeComprovante, base64: null }
            : null;

      const statusNormalizado = normalizeStatus(item?.status);
      const conformeFromHist =
        typeof lastAnalise?.conforme === "boolean"
          ? lastAnalise.conforme
          : null;
      const retornado = isRetornadoParaAnalista(lastAnalise);

      const conformeInicial = retornado
        ? null
        : conformeFromHist != null
          ? conformeFromHist
          : statusNormalizado != null
            ? statusNormalizado === "CONFORME"
            : null;

      const statusInicial = retornado
        ? null
        : (statusNormalizado ??
          (conformeInicial != null
            ? conformeInicial
              ? "CONFORME"
              : "NAO_CONFORME"
            : null));

      const obsAnalistaOriginal = lastAnalise?.obsAnalista?.trim?.() || "";
      const obsAnalistaAtual = retornado
        ? ""
        : (lastAnalise?.obsAnalista ?? "");

      return {
        _originalIndex: originalIndex,
        idAnalise: Number(lastAnalise?.id) || Number(item?.idAnalise) || 0,
        idComprovanteDespesa,
        idTipoDespesa: idTipo,
        descricao: labelTipo,
        valor:
          typeof item.valor === "number" ? item.valor : Number(item.valor) || 0,
        file: fileObj,
        identificador: item.identificador ?? uuid(),
        arquivoId: item.arquivoId ?? item.ArquivoId ?? null,
        nomeArquivo: item.nomeArquivo ?? item.nomeComprovante ?? fileObj?.name ?? "",
        nomeComprovante: item.nomeComprovante ?? item.nomeArquivo ?? fileObj?.name ?? "",
        deletar: Boolean(item.deletar) || false,
        conforme: conformeInicial,
        status: statusInicial,
        obsAnalista: obsAnalistaAtual,
        obsAnalistaOriginal,
        dataSolicOriginal: lastAnalise?.dataSolicitacao ?? null,
        historicoAnaliseAdequacaoDespesa: historico,
        correcoes: extractCorrecoes(item),
        observacaoSindicato: lastAnalise?.obsSindicato ?? "",
        solicitadoCorrecaoEm: lastAnalise?.dataSolicitacao ?? null,
        corrigidoEm: lastAnalise?.dataCorrecao ?? null,
      };
    };

    const listaNormalizada = (listaBase || []).map((it, idx) =>
      normalizar(it, idx),
    );

    listaNormalizada.sort((a, b) => {
      const ao = tipoOrderMap.get(String(a.idTipoDespesa)) ?? 9999;
      const bo = tipoOrderMap.get(String(b.idTipoDespesa)) ?? 9999;
      if (ao !== bo) return ao - bo;

      const ad = normalizeText(a.descricao);
      const bd = normalizeText(b.descricao);
      if (ad !== bd) return ad.localeCompare(bd);

      return (a._originalIndex ?? 0) - (b._originalIndex ?? 0);
    });

    setDespesas(listaNormalizada);
    initializedRef.current = true;
  }, [
    DadosDespesas,
    opcoes,
    reloadKey,
    comprovantes,
    tipoOrderMap,
    parcelaJaSolicitada,
  ]);

  useEffect(() => {
    if (!initializedRef.current) return;
    setDespesas((prev) =>
      prev.map((d) => ({
        ...d,
        descricao:
          d.descricao ||
          opcoes.find((o) => String(o.value) === String(d.idTipoDespesa))
            ?.label ||
          d.descricao,
      })),
    );
  }, [opcoes]);

  useEffect(() => {
    onChange?.(despesas);
  }, [despesas, onChange]);

  const ativos = useMemo(() => despesas.filter((d) => !d.deletar), [despesas]);
  const tudoColapsado = useMemo(
    () => ativos.every((item) => item.collapsed),
    [ativos],
  );

  const toggleCollapseById = (identificador) => {
    setDespesas((prev) =>
      prev.map((it) =>
        it.identificador === identificador
          ? { ...it, collapsed: !it.collapsed }
          : it,
      ),
    );
  };

  return (
    <Card>
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="d-flex align-items-center gap-2">
            <h5 className="fw-bold text-uppercase m-0">
              DESCRIÇÃO DAS DESPESAS
            </h5>
            <OverlayTrigger
              placement="right"
              overlay={
                <Tooltip id="tt-descricao-despesas">
                  Despesas do referido sindicato tais como aluguel, luz, água,
                  telefone, folha de pagamento, impostos, outros (referentes ao
                  exercício do ano corrente).
                </Tooltip>
              }
            >
              <span role="button" tabIndex={0} aria-label="Informações">
                <InfoCircleFill
                  size={16}
                  className="text-primary info-help-icon"
                />
              </span>
            </OverlayTrigger>
          </div>

          {!!ativos.length && (
            <div
              onClick={() =>
                setDespesas((prev) =>
                  prev.map((it) =>
                    it.deletar ? it : { ...it, collapsed: !tudoColapsado },
                  ),
                )
              }
              role="button"
              className="text-primary"
            >
              {tudoColapsado ? (
                <>
                  <ArrowsExpand /> Expandir tudo
                </>
              ) : (
                <>
                  <ArrowsCollapse /> Colapsar tudo
                </>
              )}
            </div>
          )}
        </div>
      </Card.Body>

      {ativos.length > 0 && (
        <Card.Body className="mb-4">
          {ativos.map((it, i) => (
            <div key={it.identificador} className="mt-3">
              <Expense
                it={it}
                index={i}
                onToggleCollapse={() => toggleCollapseById(it.identificador)}
                setDespesas={setDespesas}
                invalido={invalidObsIds?.has?.(it.identificador)}
                bloqueado={bloquearEdicao}
              />
            </div>
          ))}
        </Card.Body>
      )}
    </Card>
  );
}

function Expense({
  it,
  index,
  onToggleCollapse,
  setDespesas,
  invalido = false,
  bloqueado = false,
}) {
  const [showDetalhes, setShowDetalhes] = useState(false);
  const [detalheCorrecao, setDetalheCorrecao] = useState({
    solicitadoEm: null,
    corrigidoEm: null,
    obsAnalista: "",
    obsSindicato: "",
  });

  const handleValorChange = (vals) => {
    const next =
      vals.value === ""
        ? null
        : typeof vals.floatValue === "number"
          ? vals.floatValue
          : null;

    setDespesas((prev) =>
      prev.map((d) =>
        d.identificador === it.identificador ? { ...d, valor: next } : d,
      ),
    );
  };

  const handleBlurValor = () => {
    setDespesas((prev) =>
      prev.map((d) =>
        d.identificador === it.identificador
          ? { ...d, valor: d.valor ?? 0 }
          : d,
      ),
    );
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setDespesas((prev) =>
      prev.map((d) =>
        d.identificador === it.identificador
          ? { ...d, file, nomeArquivoExistente: null }
          : d,
      ),
    );
  };

  const handleStatusChange = (status) => {
    if (bloqueado) return;
    setDespesas((prev) =>
      prev.map((d) =>
        d.identificador === it.identificador
          ? {
              ...d,
              status,
              conforme: status === "CONFORME",
              _changedConformidade: true,
            }
          : d,
      ),
    );
  };

  const handleObsChange = (e) => {
    if (bloqueado) return;
    const v = e.target.value.slice(0, 1000);
    setDespesas((prev) =>
      prev.map((d) =>
        d.identificador === it.identificador ? { ...d, obsAnalista: v } : d,
      ),
    );
  };

  const normalizarCorrecao = (c) => {
    const solicitado =
      c?.solicitadoEmISO ?? c?._raw?.dataSolicitacao ?? c?.solicitadoEm;
    const corrigido =
      c?.corrigidoEmISO ?? c?._raw?.dataCorrecao ?? c?.corrigidoEm;
    return { ...c, solicitado, corrigido };
  };

  const correcoes = (
    Array.isArray(it?.correcoes)
      ? it.correcoes
      : extractCorrecoes({
          historicoAnaliseAdequacaoDespesa:
            it?.historicoAnaliseAdequacaoDespesa,
        })
  )
    .map(normalizarCorrecao)
    .map((c) => ({
      ...c,
      pendente: !!c.solicitado && !c.corrigido,
    }));

  const deveEsconder = Array.isArray(correcoes)
    ? correcoes.some((c) => !!c.solicitado && !c.corrigido) ||
      correcoes.some(
        (c) => !!c.solicitado && c.corrigido && c.correcaoRealizada,
      )
    : false;

  const ultimoHistorico = Array.isArray(it?.historicoAnaliseAdequacaoDespesa)
    ? [...it.historicoAnaliseAdequacaoDespesa].sort(
        (a, b) => Number(b?.id || 0) - Number(a?.id || 0),
      )[0]
    : null;

  const temDataSolic = !!(
    ultimoHistorico?.dataSolicitacao &&
    String(ultimoHistorico.dataSolicitacao).trim()
  );
  const temDataCorr = !!(
    ultimoHistorico?.dataCorrecao && String(ultimoHistorico.dataCorrecao).trim()
  );

  const jaCorrigidoPeloSindicato =
    temDataSolic &&
    temDataCorr &&
    ultimoHistorico?.conforme === false &&
    ultimoHistorico?.correcaoRealizada === true;

  const [loadingDownload, setLoadingDownload] = useState(false);

  const arquivoIdNumerico =
    it.arquivoId != null && Number(it.arquivoId) > 0
      ? Number(it.arquivoId)
      : null;
  const nomeArquivoExibicao =
    it.file?.name || it.nomeComprovante || it.nomeArquivo || "arquivo.pdf";

  const handleDownloadComprovante = async () => {
    try {
      setLoadingDownload(true);
      if (arquivoIdNumerico != null) {
        await downloadArquivoDespesaByArquivoId(
          arquivoIdNumerico,
          nomeArquivoExibicao,
        );
        return;
      }

      const idDownload = it.identificador ?? it.idComprovanteDespesa ?? it.id;
      if (idDownload == null) return;
      await downloadDespesaByIdentificador(idDownload, nomeArquivoExibicao);
    } catch (e) {
      console.error("Erro ao baixar comprovante:", e);
    } finally {
      setLoadingDownload(false);
    }
  };

  return (
    <div className="expensive-item-container">
      <div className="expensive-item-header d-flex justify-content-between align-items-center">
        <h4 className="mb-0">{`${index + 1}. ${it.descricao}`}</h4>
        <div className="d-flex align-items-center gap-3">
          {it.collapsed && (
            <>
              <div>
                R{"$ "}
                {Number(it.valor || 0).toLocaleString("pt-BR", {
                  minimumFractionDigits: 2,
                })}
              </div>

              {it.status && (
                <div className="d-flex align-items-center gap-2">
                  {it.status === "CONFORME" ? (
                    <span>CONFORME</span>
                  ) : (
                    <span>NÃO CONFORME</span>
                  )}
                </div>
              )}

              <div className="d-flex align-items-center gap-1">
                <FilePdf className="file-pdf-icon" /> {it.file?.name || "-"}
              </div>
            </>
          )}

          {it.collapsed ? (
            <ChevronDown
              onClick={onToggleCollapse}
              className="expensive-item-header-icon"
            />
          ) : (
            <ChevronUp
              onClick={onToggleCollapse}
              className="expensive-item-header-icon"
            />
          )}
        </div>
      </div>

      {!it.collapsed && (
        <div>
          <Row className="expensive-item-container-fields g-3 mt-2 align-items-end">
            <Col md={6}>
              <Form.Label>Valor*</Form.Label>
              <NumericFormat
                thousandSeparator="."
                decimalSeparator=","
                prefix="R$ "
                placeholder="R$ 0,00"
                value={it.valor ?? ""}
                allowNegative={false}
                allowLeadingZeros={false}
                decimalScale={2}
                fixedDecimalScale
                className="form-control"
                onValueChange={handleValorChange}
                onBlur={handleBlurValor}
                inputMode="decimal"
                onFocus={(e) => e.target.select()}
                onClick={(e) => e.target.select()}
                disabled
              />
            </Col>

            <Col md={6}>
              <Form.Label>Comprovante*</Form.Label>
              <InputGroup>
                <Button
                  className="btn-primario btn-acao"
                  disabled={loadingDownload}
                  onClick={handleDownloadComprovante}
                >
                  {loadingDownload ? "Baixando..." : "Baixar arquivo"}
                </Button>

                <Form.Control
                  placeholder="Nenhum arquivo escolhido"
                  value={it.file?.name || ""}
                  disabled
                />

                <Form.Control
                  id={`file-input-${it.identificador}`}
                  type="file"
                  accept=".pdf"
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                />
              </InputGroup>
            </Col>
          </Row>

          {correcoes.map((correcao, idx) => (
            <Row
              key={idx}
              className="expensive-item-container-fields g-2 mt-3 mb-3 align-items-center border border-1 border-gray rounded p-2"
            >
              <Col md={5}>
                <div className="d-flex flex-column">
                  <div className="d-flex align-items-center">
                    <Form.Label className="me-2 mb-0">
                      Solicitado correção em:
                    </Form.Label>
                    <Form.Control
                      type="datetime-local"
                      value={toLocalDatetimeInput(correcao.solicitadoEmISO)}
                      disabled
                      style={{ maxWidth: "400px" }}
                    />
                  </div>
                </div>
              </Col>

              <Col md={5}>
                <div className="d-flex flex-column">
                  <div className="d-flex align-items-center">
                    <Form.Label className="me-2 p-3 mb-0">
                      Corrigido em:
                    </Form.Label>
                    <Form.Control
                      type="datetime-local"
                      value={toLocalDatetimeInput(correcao.corrigidoEmISO)}
                      disabled
                      style={{ maxWidth: "400px" }}
                    />
                  </div>
                </div>
              </Col>

              <Col
                md={2}
                className="d-flex align-items-center justify-content-center"
              >
                <span
                  className="fw-semibold d-flex align-items-center text-primary"
                  style={{
                    cursor: "pointer",
                    textDecoration: "none",
                    fontSize: "16px",
                  }}
                  onClick={() => {
                    const c = it?.correcao ?? correcao ?? {};
                    setDetalheCorrecao({
                      solicitadoEm:
                        c?.solicitadoEm ??
                        c?.solicitadoCorrecaoEm ??
                        it?.solicitadoCorrecaoEm ??
                        null,
                      corrigidoEm:
                        c?.corrigidoEmISO ??
                        c?.corrigidoEm ??
                        it?.corrigidoEm ??
                        null,
                      obsAnalista: c?.obsAnalista ?? it?.obsAnalista ?? "",
                      obsSindicato:
                        c?.observacaoSindicato ??
                        c?.obsSindicato ??
                        it?.observacaoSindicato ??
                        "",
                    });
                    setShowDetalhes(true);
                  }}
                >
                  <FaEye className="me-2" />
                  Ver detalhes
                </span>
              </Col>
            </Row>
          ))}

          <Row className="expensive-item-container-fields g-3 mt-2 align-items-end">
            {deveEsconder ? (
              <div className="alert alert-warning mt-3" role="alert">
                A correção deste item já foi solicitada e está aguardando
                retorno do sindicato.
              </div>
            ) : (
              <>
                {jaCorrigidoPeloSindicato && (
                  <div className="alert alert-success mt-3" role="alert">
                    Este item foi corrigido pelo sindicato. Reavalie e marque se
                    agora está conforme ou não.
                  </div>
                )}

                <Form.Group className="mt-3">
                  <Form.Check
                    inline
                    type="radio"
                    label="CONFORME"
                    name={`status-${it.identificador}`}
                    value="CONFORME"
                    checked={it.conforme === true}
                    onChange={(e) => handleStatusChange(e.target.value)}
                    disabled={bloqueado}
                  />

                  <Form.Check
                    inline
                    type="radio"
                    label="NÃO CONFORME"
                    name={`status-${it.identificador}`}
                    value="NAO_CONFORME"
                    checked={it.conforme === false}
                    onChange={(e) => handleStatusChange(e.target.value)}
                    disabled={bloqueado}
                  />
                </Form.Group>

                <Form.Group className="mt-3">
                  <Form.Label>Observação analista:</Form.Label>
                  <Form.Control
                    id={`obs-${it.identificador}`}
                    as="textarea"
                    rows={3}
                    maxLength={1000}
                    placeholder="Adicionar texto"
                    value={it.obsAnalista || ""}
                    onChange={handleObsChange}
                    className={invalido ? "is-invalid" : ""}
                    disabled={bloqueado}
                  />
                  {invalido && (
                    <div className="invalid-feedback">
                      Informe uma observação para itens marcados como "Não
                      Conforme".
                    </div>
                  )}
                  <div className="text-muted text-end">
                    Limite máximo: 1.000 caracteres
                  </div>
                </Form.Group>
              </>
            )}
          </Row>

          <Modal
            show={showDetalhes}
            onHide={() => setShowDetalhes(false)}
            centered
            dialogClassName="modal-detalhes"
          >
            <Modal.Header closeButton>
              <Modal.Title>Detalhes da correção</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>Solicitado correção em:</Form.Label>
                  <Form.Control
                    type="text"
                    value={formatDateBR(detalheCorrecao.solicitadoEm)}
                    readOnly
                    disabled
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Corrigido em:</Form.Label>
                  <Form.Control
                    type="text"
                    value={formatDateBR(detalheCorrecao.corrigidoEm)}
                    readOnly
                    disabled
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Observação analista:</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={detalheCorrecao.obsAnalista || ""}
                    readOnly
                    disabled
                  />
                </Form.Group>

                <Form.Group>
                  <Form.Label>Observação sindicato:</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={detalheCorrecao.obsSindicato || ""}
                    readOnly
                    disabled
                  />
                </Form.Group>
              </Form>
            </Modal.Body>

            <Modal.Footer className="d-flex justify-content-center">
              <Button variant="primary" onClick={() => setShowDetalhes(false)}>
                OK
              </Button>
            </Modal.Footer>
          </Modal>
        </div>
      )}
    </div>
  );
}

export default DescricaoDespesa;
